<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import type { UsageProfile } from "@modules/usage-profile/types"

/** Valeur du panneau accordéon (unique panneau) */
const ACCORDION_PANEL_VALUE = "0"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            edit: "Modifier",
            remove: "Supprimer",
            replace: "Remplacer",
            duplicate: "Dupliquer",
            see: "Voir tous les profils",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Profil à afficher */
        profile: UsageProfile
        /** Masquer le menu d'actions */
        hideMenu?: boolean
        /** Masquer l'option Remplacer */
        hideReplace?: boolean
        /** Masquer l'option Dupliquer */
        hideDuplicate?: boolean
        /** Masquer l'option Voir tous les profils */
        hideSeeProfiles?: boolean
        /** Identifiant accordéon (pour groupement) */
        accordion?: string | null
        /** Désactiver le collapse (tout afficher) */
        noCollapse?: boolean
        /** Masquer la liste des bâtiments */
        hideBuildings?: boolean
        /** Afficher un radio pour sélectionner ce profil (ex. dans une liste) */
        selectable?: boolean
        /** Identifiant du profil sélectionné (v-model avec le parent) */
        modelValue?: string | null
        /** Name du groupe radio (quand selectable) */
        radioName?: string
    }>(),
    {
        hideMenu: false,
        hideReplace: true,
        hideDuplicate: true,
        hideSeeProfiles: true,
        accordion: null,
        noCollapse: false,
        hideBuildings: false,
        selectable: false,
        modelValue: null,
        radioName: "list-profile",
    }
)

const emit = defineEmits<{
    "click:edit": []
    "click:replace": []
    "click:remove": []
    "click:see": []
    "click:duplicate": []
    "click:open": []
    "update:modelValue": [value: string]
    "edit:week": []
    "edit:exception": []
    "edit:buildings": []
}>()

// ================================
// ÉTAT
// ================================
/** Panneau ouvert (value) ou fermé (undefined) — utilisé avec BaseAccordion */
const accordionValue = ref<string | undefined>(
    props.noCollapse ? ACCORDION_PANEL_VALUE : undefined
)

/** Émet click:open quand l'accordéon s'ouvre (pour le parent, ex. liste) */
watch(
    accordionValue,
    (value) => {
        if (value === ACCORDION_PANEL_VALUE) emit("click:open")
    },
    { immediate: false }
)

// ================================
// DONNÉES CALCULÉES
// ================================
/** Texte récapitulatif dans l'en-tête */
const getHeaderValue = computed(() => {
    const numberOfDaysOpen = props.profile.profil_semaine.filter(
        (day) =>
            day.ouvert ||
            (day.horaires_ouverture &&
                day.horaires_ouverture.length > 0 &&
                !day.ferme)
    ).length
    const numberOfExceptions =
        (props.profile.fermeture_jourferie.ferme ? 1 : 0) +
        (props.profile.fermeture_vacances.ferme ? 1 : 0)
    let str = `Ouvert ${numberOfDaysOpen}j/7 • ${numberOfExceptions} exception${
        numberOfExceptions > 1 ? "s" : ""
    }`
    if (!props.hideBuildings) {
        const numberOfBuildings = props.profile.buildings?.length ?? 0
        str += ` • ${numberOfBuildings} bâtiment${
            numberOfBuildings > 1 ? "s" : ""
        }`
    }
    return str
})

/** Modèle du menu (BaseMenu) */
const getMenuModel = computed(() => {
    const menuItems: { label: string; value: string; command: () => void }[] = [
        {
            label: t("edit"),
            value: "edit",
            command: () => emit("click:edit"),
        },
        {
            label: t("remove"),
            value: "remove",
            command: () => emit("click:remove"),
        },
    ]
    if (!props.hideReplace) {
        menuItems.splice(1, 0, {
            label: t("replace"),
            value: "replace",
            command: () => emit("click:replace"),
        })
    }
    if (!props.hideDuplicate) {
        menuItems.splice(1, 0, {
            label: t("duplicate"),
            value: "duplicate",
            command: () => emit("click:duplicate"),
        })
    }
    if (!props.hideSeeProfiles) {
        menuItems.push({
            label: t("see"),
            value: "see",
            command: () => emit("click:see"),
        })
    }
    return menuItems.map(({ label, command }) => ({ label, command }))
})

// ================================
// INTERACTIONS
// ================================
const menuRef = ref<{ toggle: (e: Event) => void } | null>(null)

/** Ouvre le menu contextuel */
function onToggleMenu(event: Event): void {
    menuRef.value?.toggle?.(event)
}

/** Notifie la sélection (radio) au parent */
function onSelectProfile(value: string): void {
    emit("update:modelValue", value)
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT -->
    <!-- ================================ -->
    <div
        class="usage-profile-item w-full flex flex-col gap-1.5"
        :data-no-collapse="noCollapse"
        :data-selectable="selectable"
    >
        <!-- Mode tout ouvert (sans accordéon) -->
        <template v-if="noCollapse">
            <header
                class="flex items-center justify-between flex-wrap gap-y-3 gap-x-2 p-3 py-0"
            >
                <div
                    v-if="selectable"
                    class="usage-profile-item__radio flex items-center shrink-0"
                >
                    <BaseRadioButton
                        :input-id="`profile-${profile._id}`"
                        :model-value="modelValue"
                        :name="radioName"
                        :value="profile._id"
                        @update:model-value="onSelectProfile"
                    />
                </div>
                <h2
                    class="text-base font-semibold m-0 text-primary-700 flex-1 min-w-0"
                >
                    {{ profile.name }}
                </h2>
                <div class="flex items-center gap-4 text-primary-700 min-h-5">
                    <p class="text-sm text-surface-700">
                        {{ getHeaderValue }}
                    </p>
                    <div
                        v-if="!hideMenu && getMenuModel.length > 0"
                        class="flex items-center"
                    >
                        <BaseButton
                            text
                            rounded
                            size="small"
                            icon="pi pi-ellipsis-v"
                            aria-haspopup="true"
                            aria-controls="usage-profile-menu"
                            @click="onToggleMenu"
                        />
                        <BaseMenu
                            ref="menuRef"
                            id="usage-profile-menu"
                            :model="getMenuModel"
                            :popup="true"
                        />
                    </div>
                </div>
            </header>
            <div :id="`profile-${profile._id}`">
                <UsageProfileItemWeek
                    class="p-0 mb-0"
                    :week-profile="profile.profil_semaine"
                    :readonly="hideMenu"
                    @click:edit="emit('edit:week')"
                />
                <UsageProfileItemException
                    class="p-0 mb-0"
                    :holiday="profile.fermeture_jourferie.ferme"
                    :vacancy="profile.fermeture_vacances.ferme"
                    :zone="profile.fermeture_vacances.zone"
                    :readonly="hideMenu"
                    @click:edit="emit('edit:exception')"
                />
                <UsageProfileItemBuildingList
                    v-if="!hideBuildings"
                    class="p-0 mb-0"
                    :name="profile.name"
                    :profile-id="profile._id"
                    :buildings="profile.buildings ?? []"
                    :readonly="hideMenu"
                    @click:edit="emit('edit:buildings')"
                />
            </div>
        </template>

        <!-- Mode repliable (BaseAccordion PrimeVue) -->
        <BaseAccordion
            v-else
            v-model:value="accordionValue"
            class="usage-profile-item__accordion w-full"
        >
            <BaseAccordionPanel :value="ACCORDION_PANEL_VALUE">
                <BaseAccordionHeader>
                    <span
                        class="flex sm:flex-row flex-col sm:items-center sm:justify-between flex-wrap gap-y-3 gap-x-2 w-full pr-2"
                    >
                        <div
                            v-if="selectable"
                            class="usage-profile-item__radio flex items-center shrink-0"
                            @click.stop
                        >
                            <BaseRadioButton
                                :input-id="`profile-${profile._id}`"
                                :model-value="modelValue"
                                :name="radioName"
                                :value="profile._id"
                                @update:model-value="onSelectProfile"
                            />
                        </div>
                        <h2
                            class="text-base font-semibold m-0 text-primary-700 flex-1 min-w-0"
                        >
                            {{ profile.name }}
                        </h2>
                        <span
                            class="flex items-center gap-4 text-primary-700 min-h-5"
                        >
                            <span class="text-sm text-surface-700">{{
                                getHeaderValue
                            }}</span>
                            <div
                                v-if="!hideMenu && getMenuModel.length > 0"
                                class="flex items-center"
                                @click.stop
                            >
                                <BaseButton
                                    text
                                    rounded
                                    size="small"
                                    icon="pi pi-ellipsis-v"
                                    aria-haspopup="true"
                                    aria-controls="usage-profile-menu"
                                    @click="onToggleMenu"
                                />
                                <BaseMenu
                                    ref="menuRef"
                                    id="usage-profile-menu"
                                    :model="getMenuModel"
                                    :popup="true"
                                />
                            </div>
                        </span>
                    </span>
                </BaseAccordionHeader>
                <BaseAccordionContent>
                    <div :id="`profile-${profile._id}`">
                        <UsageProfileItemWeek
                            class="px-3 mb-3 last:mb-0"
                            :week-profile="profile.profil_semaine"
                            :readonly="hideMenu"
                            @click:edit="emit('edit:week')"
                        />
                        <UsageProfileItemException
                            class="px-3 mb-3 last:mb-0"
                            :holiday="profile.fermeture_jourferie.ferme"
                            :vacancy="profile.fermeture_vacances.ferme"
                            :zone="profile.fermeture_vacances.zone"
                            :readonly="hideMenu"
                            @click:edit="emit('edit:exception')"
                        />
                        <UsageProfileItemBuildingList
                            v-if="!hideBuildings"
                            class="px-3 mb-3 last:mb-0"
                            :name="profile.name"
                            :profile-id="profile._id"
                            :buildings="profile.buildings ?? []"
                            :readonly="hideMenu"
                            @click:edit="emit('edit:buildings')"
                        />
                    </div>
                </BaseAccordionContent>
            </BaseAccordionPanel>
        </BaseAccordion>
    </div>
</template>

<!-- Exception : :deep() pour aligner le radio PrimeVue (impossible en Tailwind seul) -->
<style scoped>
.usage-profile-item__radio :deep(.p-radiobutton) {
    display: flex;
    align-items: center;
    margin-bottom: auto;
    min-height: 2.75rem;
    position: relative;
    top: 1px;
}
</style>
